import { Link } from "~/common/components/Link";
import RenderServerSideContent from "~/common/components/RenderServerSideContent";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { useServerSideContent } from "~/common/hooks/useServerSideContent";
import { styled } from "~/common/mui";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { LanguageSelector } from "~/features/categoryNavigation/components/LanguageSelector";
import { CountrySelector } from "~/features/forkPage/components/CountrySelector";
import { MobileForkPageNavigation } from "~/features/forkPage/components/MobileForkPageNavigation";
import type { ForkPageCountry, ForkPageNavigationProps } from "~/features/forkPage/types";
import { useTranslation } from "react-i18next";

export const ForkPageNavigation = (props: ForkPageNavigationProps) => {
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const { element: forkBackgroundEl } = useServerSideContent(`#forkBackground`);
  if (!forkBackgroundEl && !props.background) {
    return null;
  }

  return (
    <>
      {(forkBackgroundEl || props.background) && (
        <CountrySelectionContainer className="scha__fork__country__selection__container">
          {props.background ?? <RenderServerSideContent content={forkBackgroundEl} />}
          <CountrySelectorCard className="scha__fork__country__selector">
            <ForkTitle className="scha__fork__title">{props.title}</ForkTitle>
            <CountrySelector countryOptions={props.countryOptions} />
          </CountrySelectorCard>
        </CountrySelectionContainer>
      )}
      {isDesktop ? <DesktopForkPageNavigation {...props} /> : <MobileForkPageNavigation {...props} />}
    </>
  );
};

const DesktopForkPageNavigation = ({ subtitle, regions }: ForkPageNavigationProps) => {
  const { t } = useTranslation();

  const maxCountries = Math.max(...regions.map((region) => region.countries.length));
  const countriesPerColumn = Math.floor((maxCountries + 1) / 2);

  const makeLink = ({ title, url, openInNewTab }: ForkPageCountry) => (
    <Link key={title} href={url} target={openInNewTab ? "_blank" : undefined}>
      {t(title)}
    </Link>
  );

  return (
    <>
      <ForkNavContainer className="scha__fork__nav__container">
        <ForkSubtitle className="scha__fork__subtitle">{subtitle}</ForkSubtitle>
        <ForkRegions className="scha__fork__regions">
          {regions.map((region) => (
            <div className="scha__fork__region" key={region.title}>
              <RegionName className="scha__fork__region__name">{t(region.title)}</RegionName>

              <RegionCountries className="scha__fork__region__countries">
                <RegionCountriesColumn className="scha__fork__region__countries__column">
                  {region.countries.slice(0, countriesPerColumn).map(makeLink)}
                </RegionCountriesColumn>

                {region.countries.length > countriesPerColumn && (
                  <RegionCountriesColumn className="scha__fork__region__countries__column">
                    {region.countries.slice(countriesPerColumn).map(makeLink)}
                  </RegionCountriesColumn>
                )}
              </RegionCountries>
            </div>
          ))}
        </ForkRegions>
      </ForkNavContainer>
      <ForkUnderline className="scha__fork__underline fullbleed" />
      <LanguageSelector />
    </>
  );
};

const CountrySelectionContainer = styled("div")({
  position: "relative",
});

const CountrySelectorCard = styled("div")(({ theme }) => ({
  padding: `${theme.spacing(3)} ${theme.spacing(1)}`,
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  [theme.breakpoints.up("tablet")]: {
    width: 472,
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1.5),
    background: theme.palette.common.white,
    boxShadow: theme.shadows[2],
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  [theme.breakpoints.up("desktop-s")]: {
    width: 480,
    padding: theme.spacing(4),
  },
}));

const ForkTitle = styled("h1")(({ theme }) => ({
  ...theme.typography.titleLarge,
  margin: `0 0 ${theme.spacing(3)}`,
  fontWeight: 700,
  [theme.breakpoints.up("tablet")]: {
    ...theme.typography.headlineLarge,
  },
}));

const ForkSubtitle = styled("h2")(({ theme }) => ({
  ...theme.typography.titleLarge,
  margin: `0 0 ${theme.spacing(3)}`,
  fontWeight: 700,
  color: theme.palette.text.primary,
}));

const ForkUnderline = styled("hr")(({ theme }) => ({
  border: "none",
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(5),
  marginBottom: theme.spacing(4),
}));

const RegionName = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  color: theme.palette.text.secondary,
  margin: `0 0 ${theme.spacing(3)}`,
  fontWeight: 500,
}));

const ForkRegions = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(3),
  "& > *": {
    flex: 1,
    flexBasis: "100%",
    [theme.breakpoints.up("desktop-s")]: {
      flex: "1 1 auto",
    },
  },
}));

const RegionCountries = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  padding: 0,
  "& > * + *": {
    marginLeft: theme.spacing(4),
    padding: 0,
  },
}));

const RegionCountriesColumn = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  "& > * + *": {
    marginTop: theme.spacing(2),
  },
}));

const ForkNavContainer = styled("div")(({ theme }) => ({
  background: theme.palette.surface.main,
  width: "100vw",
  [theme.breakpoints.up("tablet")]: {
    marginLeft: theme.spacing(-3),
    padding: `${theme.spacing(2)} ${theme.spacing(1.5)}`,
  },
  [theme.breakpoints.up("desktop-s")]: {
    marginLeft: "calc((1208px - 100vw) / 2)",
    padding: "24px calc((100vw - 1208px) / 2)",
  },
  [theme.breakpoints.up("desktop-m")]: {
    marginLeft: "calc((1488px - 100vw) / 2)",
    padding: "24px calc((100vw - 1488px) / 2)",
  },
  [theme.breakpoints.up("desktop-l")]: {
    marginLeft: "calc((1792px - 100vw) / 2)",
    padding: "24px calc((100vw - 1792px) / 2)",
  },
}));
